import React, { useEffect, useMemo, useState } from "react";
import {
  Package, FileXls, FilePdf, Printer, Plus, FloppyDisk, Trash,
  ChartLineUp, Buildings, Calendar, MagnifyingGlass, Receipt, TrendUp,
  Warning, Lightbulb, Scales, CurrencyInr, Stack,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { api, API, fmtINR, TOKEN_KEY } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const todayISO = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

export default function InventoryCheckPage() {
  const { guard, isAdmin } = useAuth();
  const [tab, setTab] = useState("reconcile");
  const [meta, setMeta] = useState({ inventory_for_options: [], outlets: [], brands: [] });
  const [recon, setRecon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState([]);

  // Filters
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(todayISO());
  const [outlet, setOutlet] = useState("");
  const [brand, setBrand] = useState("");
  const [invFor, setInvFor] = useState("All");

  // Opening + Physical management
  const [openings, setOpenings] = useState([]);
  const [physicals, setPhysicals] = useState([]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/inventory/meta").then(({ data }) => setMeta(data)).catch(() => {});
    refreshOpenings(); refreshPhysicals(); refreshAudit();
  }, [isAdmin]);

  const refreshOpenings = () => api.get("/inventory/opening").then(({ data }) => setOpenings(data)).catch(() => {});
  const refreshPhysicals = () => api.get("/inventory/physical").then(({ data }) => setPhysicals(data)).catch(() => {});
  const refreshAudit = () => api.get("/inventory/audit").then(({ data }) => setAudit(data)).catch(() => {});

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (fromDate) p.set("from_date", fromDate);
    if (toDate) p.set("to_date", toDate);
    if (outlet) p.set("outlet", outlet);
    if (brand) p.set("brand", brand);
    if (invFor && invFor !== "All") p.set("inventory_for", invFor);
    return p.toString();
  }, [fromDate, toDate, outlet, brand, invFor]);

  const loadRecon = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/inventory/reconcile?${params}`);
      setRecon(data);
    } catch { toast.error("Failed to load reconciliation"); }
    setLoading(false);
  };

  useEffect(() => { loadRecon(); }, [params, isAdmin]); // eslint-disable-line

  const download = guard(async (url, fname) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}${url}?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Downloaded");
      refreshAudit();
    } catch { toast.error("Download failed"); }
  }, "download");

  if (!isAdmin) {
    return (
      <div className="app-shell">
        <Header />
        <main className="max-w-7xl mx-auto px-5 py-10 text-center text-sm text-[#4B5563]">Admin login required.</main>
        <Footer />
      </div>
    );
  }

  const k = recon?.kpis || {};
  const rows = recon?.rows || [];
  const health = recon?.health || {};
  const prodWise = recon?.production_wise || [];

  return (
    <div className="app-shell">
      <Header />
      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 pb-10">
          <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-medium inline-flex items-center gap-2">
            <Package size={14} weight="fill" /> Inventory Check
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-medium mt-2 leading-tight">
            Opening · Purchases · Consumption · <br className="hidden md:block" /><span className="text-[#D4AF37]">Closing.</span>
          </h1>
          <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl">
            Real-time reconciliation across every plan + purchase. Audit-ready exports built-in.
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10 space-y-6">
        <section className="kostra-card p-5" data-testid="inv-filters">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <FilterField label="From" testid="inv-from">
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 font-mono-num text-xs" />
            </FilterField>
            <FilterField label="To" testid="inv-to">
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 font-mono-num text-xs" />
            </FilterField>
            <FilterField label="Inventory For" testid="inv-for">
              <Select value={invFor} onValueChange={setInvFor}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta.inventory_for_options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </FilterField>
            <FilterField label="Outlet" testid="inv-outlet">
              <Input value={outlet} onChange={(e) => setOutlet(e.target.value)} list="inv-outlets" className="h-9 text-xs" placeholder="All" />
              <datalist id="inv-outlets">{meta.outlets?.map((o) => <option key={o} value={o} />)}</datalist>
            </FilterField>
            <FilterField label="Brand" testid="inv-brand">
              <Input value={brand} onChange={(e) => setBrand(e.target.value)} list="inv-brands" className="h-9 text-xs" placeholder="All" />
              <datalist id="inv-brands">{meta.brands?.map((o) => <option key={o} value={o} />)}</datalist>
            </FilterField>
            <FilterField label="Reset" testid="inv-reset">
              <Button onClick={() => { setFromDate(firstOfMonth()); setToDate(todayISO()); setOutlet(""); setBrand(""); setInvFor("All"); }} variant="outline" className="h-9 w-full text-xs">Reset</Button>
            </FilterField>
          </div>
        </section>

        {loading && !recon && <div className="text-center text-sm text-[#4B5563] py-10">Loading inventory…</div>}

        {recon && (
          <Tabs value={tab} onValueChange={setTab} className="space-y-6">
            <TabsList className="bg-white border border-[#E5E7EB] p-1 h-auto rounded-lg flex-wrap">
              <TabsTrigger value="reconcile" className="data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white px-4 py-2 text-sm font-semibold rounded-md" data-testid="tab-reconcile">Reconciliation</TabsTrigger>
              <TabsTrigger value="setup" className="data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white px-4 py-2 text-sm font-semibold rounded-md" data-testid="tab-setup">Opening &amp; Physical</TabsTrigger>
              <TabsTrigger value="health" className="data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white px-4 py-2 text-sm font-semibold rounded-md" data-testid="tab-health">Health</TabsTrigger>
              <TabsTrigger value="reports" className="data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white px-4 py-2 text-sm font-semibold rounded-md" data-testid="tab-reports">Download Center</TabsTrigger>
              <TabsTrigger value="audit" className="data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white px-4 py-2 text-sm font-semibold rounded-md" data-testid="tab-audit">Audit Log</TabsTrigger>
            </TabsList>

            <TabsContent value="reconcile" className="space-y-6 mt-0">
              <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="inv-kpis">
                <Kpi label="Opening" value={fmtINR(k.opening_value)} accent="#0A3B24" />
                <Kpi label="Purchases" value={fmtINR(k.purchases_value)} accent="#0E5232" />
                <Kpi label="Consumption" value={fmtINR(k.consumption_value)} accent="#D4AF37" />
                <Kpi label="Wastage" value={fmtINR(k.wastage_value)} accent="#DC2626" />
                <Kpi label="Closing" value={fmtINR(k.closing_value)} accent="#0A3B24" />
                <Kpi label="Variance" value={fmtINR(k.variance_value)} accent={k.variance_value < 0 ? "#DC2626" : "#16A34A"} />
                <Kpi label="Accuracy" value={`${k.accuracy_pct}%`} accent="#16A34A" />
                <Kpi label="Turnover" value={`${k.turnover_ratio}×`} accent="#7A9E5C" />
              </section>

              <section className="kostra-card p-5" data-testid="inv-recon-table">
                <h3 className="kostra-section-title text-lg mb-3">Inventory Reconciliation ({rows.length} ingredients)</h3>
                {rows.length === 0 ? (
                  <div className="rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-10 text-center text-sm text-[#4B5563]">
                    No inventory movement in this window. Add an Opening Stock and/or Purchases to populate.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563] border-b-2 border-[#E5E7EB]">
                          <th className="py-2 pr-2">Ingredient</th>
                          <th className="py-2 pr-2">Category</th>
                          <th className="py-2 pr-2 text-right">Opening</th>
                          <th className="py-2 pr-2 text-right">Purchases</th>
                          <th className="py-2 pr-2 text-right">Consumption</th>
                          <th className="py-2 pr-2 text-right">Wastage</th>
                          <th className="py-2 pr-2 text-right bg-[#FDF9EE]/40">Closing</th>
                          <th className="py-2 pr-2 text-right">Physical</th>
                          <th className="py-2 pl-2 text-right">Variance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={r.key || i} className="border-b border-[#F1F2F4]" data-testid={`inv-row-${i}`}>
                            <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{r.name}</td>
                            <td className="py-2 pr-2 text-xs text-[#4B5563]">{r.category}</td>
                            <td className="py-2 pr-2 text-right font-mono-num">{r.opening_kg.toFixed(3)}</td>
                            <td className="py-2 pr-2 text-right font-mono-num">{r.purchases_kg.toFixed(3)}</td>
                            <td className="py-2 pr-2 text-right font-mono-num text-[#D4AF37]">{r.consumption_kg.toFixed(3)}</td>
                            <td className="py-2 pr-2 text-right font-mono-num text-[#DC2626]">{r.wastage_kg.toFixed(3)}</td>
                            <td className="py-2 pr-2 text-right font-mono-num font-bold text-[#0A3B24] bg-[#FDF9EE]/30">{r.closing_kg.toFixed(3)}</td>
                            <td className="py-2 pr-2 text-right font-mono-num text-[#4B5563]">{r.physical_kg !== null ? r.physical_kg.toFixed(3) : "—"}</td>
                            <td className="py-2 pl-2 text-right font-mono-num font-semibold" style={{ color: (r.variance_kg || 0) < 0 ? "#DC2626" : (r.variance_kg || 0) > 0 ? "#16A34A" : "#4B5563" }}>
                              {r.physical_kg !== null ? `${r.variance_kg > 0 ? "+" : ""}${r.variance_kg.toFixed(3)}` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {prodWise.length > 0 && (
                <section className="kostra-card p-5" data-testid="inv-prodwise">
                  <h3 className="kostra-section-title text-lg mb-3">Production-wise Inventory Analysis</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                        <th className="py-2 pr-2">Production For</th>
                        <th className="py-2 pr-2 text-right">Purchases</th>
                        <th className="py-2 pr-2 text-right">Consumption</th>
                        <th className="py-2 pr-2 text-right">Wastage</th>
                        <th className="py-2 pl-2 text-right">Inventory Impact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prodWise.map((p, i) => (
                        <tr key={i} className="border-b border-[#F1F2F4]" data-testid={`inv-prod-${i}`}>
                          <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{p.production_for}</td>
                          <td className="py-2 pr-2 text-right font-mono-num">{fmtINR(p.purchases_value)}</td>
                          <td className="py-2 pr-2 text-right font-mono-num">{fmtINR(p.consumption_value)}</td>
                          <td className="py-2 pr-2 text-right font-mono-num text-[#DC2626]">{fmtINR(p.wastage_value)}</td>
                          <td className="py-2 pl-2 text-right font-mono-num font-semibold" style={{ color: p.inventory_impact < 0 ? "#DC2626" : "#16A34A" }}>{fmtINR(p.inventory_impact)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
            </TabsContent>

            <TabsContent value="setup" className="space-y-6 mt-0">
              <OpeningStockSection openings={openings} onRefresh={refreshOpenings} onLoadRecon={loadRecon} outlet={outlet} brand={brand} />
              <PhysicalStockSection physicals={physicals} onRefresh={refreshPhysicals} onLoadRecon={loadRecon} outlet={outlet} brand={brand} />
            </TabsContent>

            <TabsContent value="health" className="space-y-4 mt-0" data-testid="inv-health">
              <HealthBlock title="Fast Movers" rows={health.fast_movers || []} accent="#16A34A" cols={[["name", "Ingredient"], ["consumption_kg", "Consumed (kg)"], ["closing_kg", "Closing (kg)"]]} />
              <HealthBlock title="Slow Movers" rows={health.slow_movers || []} accent="#A66B00" cols={[["name", "Ingredient"], ["consumption_kg", "Consumed (kg)"], ["closing_kg", "Closing (kg)"]]} />
              <HealthBlock title="Dead Stock" rows={health.dead_stock || []} accent="#DC2626" cols={[["name", "Ingredient"], ["opening_kg", "Opening (kg)"], ["closing_kg", "Closing (kg)"]]} />
              <HealthBlock title="Near Reorder" rows={health.reorder_alerts || []} accent="#D4AF37" cols={[["name", "Ingredient"], ["closing_kg", "Closing (kg)"], ["consumption_kg", "Consumed (kg)"]]} />
              <HealthBlock title="High Wastage" rows={health.high_wastage || []} accent="#DC2626" cols={[["name", "Ingredient"], ["wastage_kg", "Wasted (kg)"], ["wastage_value", "Wastage ₹"]]} />
            </TabsContent>

            <TabsContent value="reports" className="mt-0">
              <section className="kostra-card p-5" data-testid="inv-downloads">
                <h3 className="kostra-section-title text-lg mb-3">Download Center</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    ["Inventory Reconciliation", "/inventory/reports/reconciliation/excel", "InventoryReconciliation.xlsx", "xls", "inv-dl-reconciliation"],
                    ["Inventory Valuation", "/inventory/reports/valuation/excel", "InventoryValuation.xlsx", "xls", "inv-dl-valuation"],
                    ["Stock Verification", "/inventory/reports/verification/excel", "StockVerification.xlsx", "xls", "inv-dl-verification"],
                    ["Variance Report", "/inventory/reports/variance/excel", "InventoryVariance.xlsx", "xls", "inv-dl-variance"],
                    ["Production Consumption", "/inventory/reports/consumption/excel", "ProductionConsumption.xlsx", "xls", "inv-dl-consumption"],
                    ["Wastage Analysis", "/inventory/reports/wastage/excel", "WastageAnalysis.xlsx", "xls", "inv-dl-wastage"],
                    ["Monthly Inventory Summary", "/inventory/reports/monthly-summary/excel", "MonthlyInventorySummary.xlsx", "xls", "inv-dl-monthly"],
                    ["Inventory Health Report", "/inventory/reports/health/pdf", "InventoryHealth.pdf", "pdf", "inv-dl-health"],
                    ["Management MIS Report", "/inventory/reports/mis/pdf", "ManagementMIS.pdf", "pdf", "inv-dl-mis"],
                  ].map(([label, url, fname, fmt, testid]) => (
                    <Button key={url} onClick={() => download(url, fname)}
                            className={fmt === "pdf"
                              ? "bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold justify-start"
                              : "bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold justify-start"}
                            data-testid={testid}>
                      {fmt === "pdf" ? <FilePdf size={14} weight="bold" className="mr-2" /> : <FileXls size={14} weight="bold" className="mr-2" />}
                      {label}
                    </Button>
                  ))}
                </div>
                <Button onClick={() => window.print()} variant="outline" className="mt-3" data-testid="inv-print">
                  <Printer size={14} weight="bold" className="mr-2" /> Print Inventory Check Sheet
                </Button>
              </section>
            </TabsContent>

            <TabsContent value="audit" className="mt-0">
              <section className="kostra-card p-5" data-testid="inv-audit">
                <h3 className="kostra-section-title text-lg mb-3">Download History ({audit.length})</h3>
                {audit.length === 0 ? (
                  <div className="rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-10 text-center text-sm text-[#4B5563]">No downloads yet</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
                        <th className="py-2 pr-2">Date &amp; Time</th>
                        <th className="py-2 pr-2">User</th>
                        <th className="py-2 pr-2">Report</th>
                        <th className="py-2 pr-2">Format</th>
                        <th className="py-2">Filters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audit.map((a, i) => (
                        <tr key={a.id} className="border-b border-[#F1F2F4]" data-testid={`inv-audit-${i}`}>
                          <td className="py-2 pr-2 font-mono-num text-xs">{new Date(a.at).toLocaleString()}</td>
                          <td className="py-2 pr-2 font-semibold text-[#0A3B24]">{a.user}</td>
                          <td className="py-2 pr-2 capitalize">{a.report.replace(/_/g, " ")}</td>
                          <td className="py-2 pr-2 uppercase text-xs font-bold text-[#D4AF37]">{a.format}</td>
                          <td className="py-2 text-xs text-[#4B5563]">{Object.entries(a.filters || {}).map(([k, v]) => `${k}=${v}`).join(" · ") || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </TabsContent>
          </Tabs>
        )}
      </main>
      <Footer />
    </div>
  );
}

function FilterField({ label, testid, children }) {
  return (
    <div>
      <Label className="kostra-data-label">{label}</Label>
      <div className="mt-1" data-testid={testid}>{children}</div>
    </div>
  );
}

function Kpi({ label, value, accent = "#111827" }) {
  return (
    <div className="kostra-card p-3">
      <div className="kostra-data-label text-[10px]">{label}</div>
      <div className="font-mono-num text-base font-bold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function HealthBlock({ title, rows, accent, cols }) {
  if (!rows.length) return null;
  return (
    <div className="kostra-card p-5">
      <h4 className="kostra-section-title text-base mb-3" style={{ color: accent }}>{title}</h4>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
            {cols.map(([_, label]) => <th key={label} className="py-1 pr-2">{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[#F1F2F4]">
              {cols.map(([k]) => (
                <td key={k} className="py-1 pr-2 font-mono-num">
                  {typeof r[k] === "number" ? (k.endsWith("_value") ? fmtINR(r[k]) : r[k].toFixed(3)) : r[k]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Opening Stock CRUD section ----------
function OpeningStockSection({ openings, onRefresh, onLoadRecon, outlet, brand }) {
  const { guard } = useAuth();
  const [periodStart, setPeriodStart] = useState(firstOfMonth());
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);

  const reset = () => { setEditId(null); setItems([]); setPeriodStart(firstOfMonth()); };
  const addRow = () => setItems([...items, { id: crypto.randomUUID(), name: "", qty_kg: 0, rate_per_kg: 0 }]);
  const upd = (i, p) => setItems(items.map((x, idx) => idx === i ? { ...x, ...p } : x));
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  const save = guard(async () => {
    if (!items.length) { toast.error("Add at least one ingredient"); return; }
    try {
      const payload = { outlet, brand, period_start: periodStart, items };
      if (editId) await api.put(`/inventory/opening/${editId}`, payload);
      else await api.post("/inventory/opening", payload);
      toast.success("Opening stock saved"); reset(); onRefresh(); onLoadRecon();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  }, "save opening stock");

  const carryForward = guard(async () => {
    if (!periodStart) { toast.error("Pick a Period Start to carry forward into"); return; }
    if (!window.confirm(`Carry forward previous period's closing into ${periodStart}? Any existing opening at this date for outlet=${outlet || "All"}, brand=${brand || "All"} will be overwritten.`)) return;
    try {
      const { data } = await api.post("/inventory/opening/carry-forward", {
        outlet, brand, next_period_start: periodStart,
      });
      const newItems = (data?.items || []).map((it) => ({ id: crypto.randomUUID(), ...it }));
      setItems(newItems);
      setEditId(data?.id || null);
      toast.success(`Carried forward ${newItems.length} ingredient(s)`);
      onRefresh(); onLoadRecon();
    } catch (e) { toast.error(e?.response?.data?.detail || "Carry forward failed"); }
  }, "carry forward opening");

  const load = (o) => { setEditId(o.id); setPeriodStart(o.period_start); setItems((o.items || []).map((it) => ({ id: crypto.randomUUID(), ...it }))); };
  const del = guard(async (id) => {
    if (!window.confirm("Delete this opening stock record?")) return;
    await api.delete(`/inventory/opening/${id}`); toast.success("Deleted"); onRefresh(); onLoadRecon();
  }, "delete opening");

  return (
    <section className="kostra-card p-5" data-testid="inv-opening-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="kostra-section-title text-lg">Opening Stock</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {editId && <Button variant="outline" onClick={reset} className="h-9 text-xs">New</Button>}
          <Button
            onClick={carryForward}
            variant="outline"
            className="h-9 text-xs border-[#D4AF37]/40 bg-[#FDF9EE] text-[#0A3B24] hover:bg-[#F4E9C5]"
            data-testid="inv-open-carry-forward"
            title="Auto-fill from the previous period's closing balance"
          >
            <TrendUp size={14} className="mr-1" />Carry Forward
          </Button>
          <Button onClick={addRow} variant="outline" className="h-9 text-xs" data-testid="inv-open-add-row"><Plus size={14} className="mr-1" />Add Ingredient</Button>
          <Button onClick={save} className="bg-[#0A3B24] hover:bg-[#0E5232] text-white h-9 text-xs" data-testid="inv-open-save"><FloppyDisk size={14} className="mr-1" />Save</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <Label className="kostra-data-label">Period Start</Label>
          <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="mt-1 h-9 font-mono-num" data-testid="inv-open-period" />
        </div>
        <div className="md:col-span-2 text-xs text-[#4B5563] flex items-end pb-2">
          Apply to outlet=<b className="ml-1">{outlet || "All"}</b> · brand=<b className="ml-1">{brand || "All"}</b>
        </div>
      </div>
      {items.length > 0 && (
        <table className="w-full text-sm mb-4">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
              <th className="py-2 pr-2">Ingredient</th>
              <th className="py-2 pr-2 w-32 text-right">Qty (kg)</th>
              <th className="py-2 pr-2 w-32 text-right">Rate / Kg</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.id} className="border-b border-[#F1F2F4]">
                <td className="py-1 pr-2"><Input value={it.name} onChange={(e) => upd(i, { name: e.target.value })} className="h-8 text-xs" /></td>
                <td className="py-1 pr-2"><Input type="number" min="0" step="0.1" value={it.qty_kg} onChange={(e) => upd(i, { qty_kg: parseFloat(e.target.value) || 0 })} className="h-8 text-xs text-right font-mono-num" /></td>
                <td className="py-1 pr-2"><Input type="number" min="0" step="1" value={it.rate_per_kg} onChange={(e) => upd(i, { rate_per_kg: parseFloat(e.target.value) || 0 })} className="h-8 text-xs text-right font-mono-num" /></td>
                <td><button onClick={() => remove(i)} className="text-[#DC2626] hover:bg-[#FBE8E8] rounded p-1"><Trash size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="text-xs font-semibold text-[#0A3B24] mb-2">Saved openings ({openings.length})</div>
      <div className="space-y-2">
        {openings.map((o) => (
          <div key={o.id} className="flex items-center justify-between p-2 rounded border border-[#E5E7EB] text-xs" data-testid={`inv-open-row-${o.id}`}>
            <div>
              <span className="font-bold font-mono-num">{o.period_start}</span> · {o.outlet || "—"} · {o.brand || "—"} · {(o.items || []).length} items
            </div>
            <div className="flex gap-1">
              <button onClick={() => load(o)} className="px-2 py-0.5 rounded bg-[#FDF9EE] text-[#0A3B24] hover:bg-[#F4E9C5]">Edit</button>
              <button onClick={() => del(o.id)} className="px-2 py-0.5 rounded bg-[#FBE8E8] text-[#DC2626] hover:bg-[#F8C5C5]">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------- Physical Stock take ----------
function PhysicalStockSection({ physicals, onRefresh, onLoadRecon, outlet, brand }) {
  const { guard } = useAuth();
  const [takenAt, setTakenAt] = useState(todayISO());
  const [items, setItems] = useState([]);

  const addRow = () => setItems([...items, { id: crypto.randomUUID(), name: "", physical_qty_kg: 0 }]);
  const upd = (i, p) => setItems(items.map((x, idx) => idx === i ? { ...x, ...p } : x));
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  const save = guard(async () => {
    if (!items.length) { toast.error("Add at least one ingredient"); return; }
    try {
      await api.post("/inventory/physical", { outlet, brand, taken_at: takenAt, items });
      toast.success("Physical stock saved"); setItems([]); onRefresh(); onLoadRecon();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  }, "save physical stock");
  const del = guard(async (id) => {
    if (!window.confirm("Delete this stock take?")) return;
    await api.delete(`/inventory/physical/${id}`); toast.success("Deleted"); onRefresh(); onLoadRecon();
  }, "delete physical");

  return (
    <section className="kostra-card p-5" data-testid="inv-physical-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="kostra-section-title text-lg">Physical Stock Take</h3>
        <div className="flex items-center gap-2">
          <Button onClick={addRow} variant="outline" className="h-9 text-xs" data-testid="inv-phys-add-row"><Plus size={14} className="mr-1" />Add</Button>
          <Button onClick={save} className="bg-[#0A3B24] hover:bg-[#0E5232] text-white h-9 text-xs" data-testid="inv-phys-save"><FloppyDisk size={14} className="mr-1" />Save</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <Label className="kostra-data-label">Taken At</Label>
          <Input type="date" value={takenAt} onChange={(e) => setTakenAt(e.target.value)} className="mt-1 h-9 font-mono-num" data-testid="inv-phys-date" />
        </div>
      </div>
      {items.length > 0 && (
        <table className="w-full text-sm mb-4">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563] border-b border-[#E5E7EB]">
              <th className="py-2 pr-2">Ingredient</th>
              <th className="py-2 pr-2 w-40 text-right">Physical Qty (kg)</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.id} className="border-b border-[#F1F2F4]">
                <td className="py-1 pr-2"><Input value={it.name} onChange={(e) => upd(i, { name: e.target.value })} className="h-8 text-xs" /></td>
                <td className="py-1 pr-2"><Input type="number" min="0" step="0.1" value={it.physical_qty_kg} onChange={(e) => upd(i, { physical_qty_kg: parseFloat(e.target.value) || 0 })} className="h-8 text-xs text-right font-mono-num" /></td>
                <td><button onClick={() => remove(i)} className="text-[#DC2626] hover:bg-[#FBE8E8] rounded p-1"><Trash size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="text-xs font-semibold text-[#0A3B24] mb-2">Saved stock-takes ({physicals.length})</div>
      <div className="space-y-2">
        {physicals.map((p) => (
          <div key={p.id} className="flex items-center justify-between p-2 rounded border border-[#E5E7EB] text-xs" data-testid={`inv-phys-row-${p.id}`}>
            <div><span className="font-bold font-mono-num">{p.taken_at}</span> · {p.outlet || "—"} · {(p.items || []).length} items</div>
            <button onClick={() => del(p.id)} className="px-2 py-0.5 rounded bg-[#FBE8E8] text-[#DC2626] hover:bg-[#F8C5C5]">Delete</button>
          </div>
        ))}
      </div>
    </section>
  );
}
